import { useQuery } from "@tanstack/react-query";
import { capitalMarketsDashboardApi } from "@/api/capitalMarketsDashboard";
import { collectionPackagesApi } from "@/api/collectionPackages";
import { packageStatusLabel } from "@/components/Collection/collectionStatusLabels";
import { reportsApi } from "@/api/reports";
import { formatPercent } from "@/components/portfolioAdmin/metrics/MetricsCards";
import { formatCurrency } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import { WidgetFrame } from "../../WidgetFrame";
import type { DashboardWidgetProps } from "../../types";
import { PanelFigures, PanelRows } from "./shared";

/**
 * Cross-module panels: outstanding document collections, the caller's
 * recent LLM reports, and the capital-markets KPI tile.
 */

export function CollectionsOutstandingWidget({ widget }: DashboardWidgetProps) {
  const queue = useQuery({
    queryKey: ["dashboard", "collections-outstanding"],
    queryFn: () => collectionPackagesApi.listOutstanding(),
  });
  const rows = (queue.data ?? []).slice(0, 6);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/collection/packages"
      isLoading={queue.isLoading}
      isError={queue.isError}
      isEmpty={rows.length === 0}
      emptyText="No outstanding collection packages."
    >
      <PanelRows
        items={rows.map((p) => ({
          id: p.id,
          primary: p.recipientFullName || p.recipientEmail,
          secondary: `${p.itemsCompleted}/${p.itemsTotal} items`,
          badge: packageStatusLabel(p.status),
          badgeTone: "progress",
          timestamp: p.dueDate ?? p.sentAt ?? p.createdAt,
          href: `/collection/packages/${p.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

export function MyRecentReportsWidget({ widget }: DashboardWidgetProps) {
  const { user } = useAuth();
  const reports = useQuery({
    queryKey: ["dashboard", "my-recent-reports"],
    queryFn: () => reportsApi.list({ page: 1, pageSize: 20 }),
  });
  // The list endpoint is permission-scoped, not user-scoped — narrow to mine.
  const rows = (reports.data?.data ?? [])
    .filter((r) => !user?.id || r.userId === user.id)
    .slice(0, 5);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/reports"
      isLoading={reports.isLoading}
      isError={reports.isError}
      isEmpty={rows.length === 0}
      emptyText="You haven't generated any reports yet."
    >
      <PanelRows
        items={rows.map((r) => ({
          id: r.id,
          primary: r.title,
          secondary: r.promptTemplateName,
          badge: r.generationStage ?? null,
          timestamp: r.createdAt,
          href: `/reports/${r.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

export function CapitalMarketsTileWidget({ widget }: DashboardWidgetProps) {
  const kpis = useQuery({
    queryKey: ["dashboard", "capital-markets-kpis"],
    queryFn: () => capitalMarketsDashboardApi.topKpis(),
  });
  const data = kpis.data;

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/capital-markets"
      isLoading={kpis.isLoading}
      isError={kpis.isError}
    >
      {data && (
        <PanelFigures
          figures={[
            { label: "total AUM", value: formatCurrency(data.totalAum) },
            { label: "committed", value: formatPercent(data.aggregateInvestmentFillPct, 0) },
            { label: "active pools", value: data.activePoolCount },
            { label: "investors", value: data.investorCount },
          ]}
        />
      )}
    </WidgetFrame>
  );
}
